// "Where to stay" links: Stay22 Allez deep links (stay22.com/allez/roam) that
// open a hotel search around a point, for given dates, on whichever booking
// site (Booking.com, Expedia, Hotels.com…) Stay22 routes the visitor to.
// Plain links only — no Stay22 script on the page (it rewrites links in the
// DOM, loads trackers and is blocked by EasyPrivacy anyway). Every click goes
// through openExternal with `kind: 'hotel'`, like the other affiliate CTAs.
// Stay22's map widget is an iframe the app loads only on the visitor's click,
// so the page itself carries none of Stay22's code.
// The site's aid, centroids and map color: SITE.stay22 (site-config.ts).
// Params: https://dev.stay22.com/docs/allez/parameters

import type { Locale } from './i18n/locales';
import type { SiteConfig } from './site-config';
import type { DbEvent } from './supabase';
import { SITE } from '@site';

const SITE_CONFIG: SiteConfig = SITE;

function stay22Config(): NonNullable<SiteConfig['stay22']> {
  const config = SITE_CONFIG.stay22;
  if (!config) throw new Error(`${SITE_CONFIG.id} has no SITE.stay22 — hotel links are off on this site`);
  return config;
}

/** Value of the `source` property on the Umami `affiliate-click` event. */
export const STAY22_SOURCE = 'stay22';

/** A hotel search: where, and optionally which nights (YYYY-MM-DD, checkout exclusive). */
export type Stay = {
  lat: number;
  lng: number;
  checkin?: string;
  checkout?: string;
  /** The point is the venue's own, not its city's centroid (copy can say "near the venue"). */
  atVenue?: boolean;
};

// Longer than this and it's a run (an exhibition, a season), not a trip to
// one event — the search then leaves the dates to the visitor.
const MAX_STAY_NIGHTS = 7;

/** Calendar day (YYYY-MM-DD) of an instant in `timeZone`. */
function ymd(date: Date, timeZone: string): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function addDays(day: string, n: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + n);
  return date.toISOString().slice(0, 10);
}

function nightsBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** The city's `cities.centroid` (SITE.stay22.centroids), for pages that only know the city. */
export function cityStay(cityId: string | null | undefined): Stay | null {
  const point = cityId ? stay22Config().centroids[cityId] : undefined;
  return point ? { ...point } : null;
}

/**
 * Around the venue, for the night of the event — or every night of a multi-day
 * one (a festival weekend), checking out the day after it ends. A run longer
 * than a week gets no dates. Pure function of the row, so the caption built
 * from it is the same at export time and in the browser.
 */
export function eventStay(event: DbEvent): Stay | null {
  const centroid = cityStay(event.city_id);
  const venue =
    event.venue_lat != null && event.venue_lng != null
      ? { lat: event.venue_lat, lng: event.venue_lng }
      : null;
  const point = venue
    ? {
        ...venue,
        // A feed without venue coords gets the city centroid in the DB too.
        atVenue: !(centroid && centroid.lat === venue.lat && centroid.lng === venue.lng),
      }
    : centroid;
  if (!point) return null;

  const start = new Date(event.start_ts);
  const checkin = ymd(start, event.tz);
  const end = event.end_ts ? new Date(event.end_ts) : null;
  // Under 24 hours is one evening, even when it ends after midnight.
  if (!end || end.getTime() - start.getTime() < 86400000) {
    return { ...point, checkin, checkout: addDays(checkin, 1) };
  }
  const checkout = addDays(ymd(end, event.tz), 1);
  if (nightsBetween(checkin, checkout) > MAX_STAY_NIGHTS) return point;
  return { ...point, checkin, checkout };
}

// aid, point, campaign and the stay's dates — settled against today (the
// site's timezone): a check-in already past moves to today while nights
// remain, else the dates are dropped, since booking sites reject past dates.
// Hence callers build the URL on click / on open, never at render.
function baseParams(stay: Stay, campaign: string, now: Date): URLSearchParams {
  const params = new URLSearchParams({
    aid: stay22Config().aid,
    lat: String(stay.lat),
    lng: String(stay.lng),
    // Underscores, not hyphens: Stay22's reports split campaign ids on '-'.
    campaign: campaign.replace(/-/g, '_'),
  });
  if (stay.checkin && stay.checkout) {
    const today = ymd(now, SITE_CONFIG.timeZone);
    const checkin = stay.checkin < today ? today : stay.checkin;
    if (checkin < stay.checkout) {
      params.set('checkin', checkin);
      params.set('checkout', stay.checkout);
    }
  }
  return params;
}

/** The Allez link: a hotel search on the booking site Stay22 picks. */
export function stay22Url(
  stay: Stay,
  { campaign, locale }: { campaign: string; locale: Locale },
  now: Date = new Date(),
): string {
  const params = baseParams(stay, campaign, now);
  params.set('lang', locale);
  return `https://www.stay22.com/allez/roam?${params.toString()}`;
}

// Languages the map widget speaks (`ljs`, https://dev.stay22.com/docs/maps/parameters);
// Russian isn't one, so /ru lets it auto-detect from the browser.
const MAP_LANGUAGES: readonly string[] = [
  'en', 'fr', 'de', 'es', 'pt', 'it', 'nl', 'pl', 'zh', 'ja', 'cs', 'no', 'tr', 'sv', 'uk',
];

/**
 * Stay22's map widget (an iframe — https://dev.stay22.com/docs/maps/parameters):
 * hotels and rentals around the point, each linking out through Allez. Its own
 * `campaign` suffix (`_map`) keeps its bookings apart from the button's in
 * Stay22's reports — the clicks inside the iframe never reach Umami.
 * `hideMainMarker` drops the pin on `stay` where it marks nothing.
 */
export function stay22MapUrl(
  stay: Stay,
  {
    campaign,
    locale,
    zoom,
    hideMainMarker,
  }: { campaign: string; locale: Locale; zoom: number; hideMainMarker?: boolean },
  now: Date = new Date(),
): string {
  const params = baseParams(stay, `${campaign}_map`, now);
  params.set('zoom', String(zoom));
  if (hideMainMarker) params.set('hidemainmarker', 'true');
  params.set('currency', SITE_CONFIG.currency);
  if (MAP_LANGUAGES.includes(locale)) params.set('ljs', locale);
  // The site's accent, so the markers read as ours.
  params.set('maincolor', stay22Config().mapColor);
  return `https://www.stay22.com/embed/gm?${params.toString()}`;
}
